/**
 * Kanala geri yazma sözleşmesi (ADR 2026-10-30-kanala-geri-yazma).
 *
 * Bu dosya yalnız sabitleri ve saf yardımcıları taşır. Yazma KENDİSİ burada yok: iş satırı
 * (`ChannelWriteJob`) sipariş durumundan türetilen bir NİYETtir; deneme sonuçları
 * `ChannelPushAttempt` defterindedir (tek kaynak). Eylem sözlüğü `channel-push.ts`'tedir
 * (yeni eylem adı yok).
 */
import type { ChannelPushAction } from './channel-push';

/** Yazma yapılabilen kanallar (`OrderSource` alt kümesi). */
export const CHANNEL_WRITE_SOURCES = ['HAYAT_PANEL', 'ABANT_PANEL', 'GETIR_SU'] as const;
export type ChannelWriteSource = (typeof CHANNEL_WRITE_SOURCES)[number];

export function isChannelWriteSource(source: string): source is ChannelWriteSource {
  return (CHANNEL_WRITE_SOURCES as readonly string[]).includes(source);
}

/**
 * Kanal başına kapılar (kural 10 kill switch ile aynı yer: `Tenant.flags`, manifest `flags`).
 * `scraper` kapalıysa yazma da durur; `write` yazma yetkisidir (varsayılan kapalı);
 * `cancel` iptal yazmasının AYRI yetkisidir.
 */
export const CHANNEL_WRITE_FLAGS = {
  HAYAT_PANEL: { scraper: 'scraperHayat', write: 'writeHayat', cancel: 'writeHayatCancel' },
  ABANT_PANEL: { scraper: 'scraperAbant', write: 'writeAbant', cancel: 'writeAbantCancel' },
  GETIR_SU: { scraper: 'scraperGetir', write: 'writeGetir', cancel: 'writeGetirCancel' },
} as const satisfies Record<ChannelWriteSource, { scraper: string; write: string; cancel: string }>;

/** `Tenant.flags` içinde yazma yetkisi taşıyan anahtarlar (yalnız OWNER açar). */
export const CHANNEL_WRITE_FLAG_KEYS = CHANNEL_WRITE_SOURCES.flatMap((s) => [
  CHANNEL_WRITE_FLAGS[s].write,
  CHANNEL_WRITE_FLAGS[s].cancel,
]);

/** Kanal başına yazma kipinin tutulduğu `Tenant.settings` anahtarı (Hayat: mevcut `hayatWriteMode`). */
export const CHANNEL_WRITE_MODE_KEYS = {
  HAYAT_PANEL: 'hayatWriteMode',
  ABANT_PANEL: 'abantWriteMode',
  GETIR_SU: 'getirWriteMode',
} as const satisfies Record<ChannelWriteSource, string>;

/** Araca İletildi için atamadan sonra beklenecek dakika (`channelWriteOnRouteDelayMin`). */
export const CHANNEL_WRITE_ON_ROUTE_DELAY_DEFAULT_MIN = 10;

/** İş satırı ve deneme defteri saklama süresi (gün); KVKK işi önce deneme, sonra iş siler. */
export const CHANNEL_WRITE_JOB_RETENTION_DAYS = 180;

/**
 * İş durumları (DB'de String + bu liste; enum migration istemesin).
 *  Açık : WAITING (vade/sefer/oturum bekler) · AWAITING_APPROVAL · READY · RUNNING
 *  Kapalı: DONE · SKIPPED (kanal bizden ileride / zaten yapılmış) · SUPERSEDED (niyet değişti)
 *          · EXPIRED (gün bitti) · STOPPED (belirsiz sonuç / deneme tavanı; elle devam)
 *          · CANCELLED (OWNER vazgeçti / onayı reddetti)
 */
export const CHANNEL_WRITE_JOB_STATUSES = [
  'WAITING',
  'AWAITING_APPROVAL',
  'READY',
  'RUNNING',
  'DONE',
  'SKIPPED',
  'SUPERSEDED',
  'EXPIRED',
  'STOPPED',
  'CANCELLED',
] as const;
export type ChannelWriteJobStatus = (typeof CHANNEL_WRITE_JOB_STATUSES)[number];

export const CHANNEL_WRITE_OPEN_STATUSES = [
  'WAITING',
  'AWAITING_APPROVAL',
  'READY',
  'RUNNING',
] as const satisfies readonly ChannelWriteJobStatus[];

/** Kullanıcının/uygulamanın niyeti geçersiz kılabileceği (RUNNING hariç) açık durumlar. */
export const CHANNEL_WRITE_SUPERSEDABLE_STATUSES = [
  'WAITING',
  'AWAITING_APPROVAL',
  'READY',
] as const satisfies readonly ChannelWriteJobStatus[];

/**
 * Sabit hata/neden kodları (`ChannelWriteJob.lastErrorCode`, `ChannelPushAttempt.error`).
 * Ham kanal mesajı ASLA yazılmaz. Panelde `t('channelWrite.error.<kod>')`.
 */
export const CHANNEL_WRITE_ERROR_CODES = [
  'writer-not-configured',
  'writer-disabled',
  'scraper-disabled',
  'tenant-restricted',
  'unknown-action',
  'session-not-ready',
  'session-rejected',
  // Y3 (Getir): şoför → Getir kurye eşlemesi yok. Hata değil, bekleme: eşleme girilene kadar
  // yazılmaz; iş tavana sayılmaz (bekler).
  'courier-unmapped',
  'external-ahead',
  'already-done',
  'last-attempt-uncertain',
  'retry-limit',
  'writer-threw',
  'writer-failed',
  'no-open-trip',
  // Abant yazıcısının kendi sabit kodları (Y2): hepsi "istek GİTMEDİ" sınıfıdır,
  // panelden gelen ham metin ASLA defterye girmez.
  'employee-mapping-missing',
  'send-not-done',
  'payment-type-missing',
  'order-not-found',
  'external-id-invalid',
  'expired-day-end',
  'expired-stale',
  // Hayat: Teslim Edildi, Araca İletildi'den önce yazılamaz (panel "Sadece Araca İletilen
  // Siparişler Tamamlanabilir" der); yazıcı POST'a çıkmadan ya da ret gövdesinden döner.
  'needs-send-first',
] as const;
export type ChannelWriteErrorCode = (typeof CHANNEL_WRITE_ERROR_CODES)[number];

/** Sipariş detayında/mobilde görünen yazma durumu. */
export const CHANNEL_WRITE_STATES = [
  'none',
  'pending',
  'awaiting_approval',
  'written',
  'failed',
  'disabled',
] as const;
export type ChannelWriteState = (typeof CHANNEL_WRITE_STATES)[number];

export interface ChannelWriteView {
  state: ChannelWriteState;
  /** Görünümün dayandığı iş eylemi (CHANNEL_PUSH_ACTIONS); iş yoksa yok. Şoföre dönmez. */
  action?: string;
  lastAttemptAt?: string;
  errorCode?: string;
}

/**
 * Aynı siparişte aşama sırası: önceki aşamanın açık işi varken sonraki aşama yürütülmez
 * (araç onayı bitmeden "Araca İletildi" gitmez).
 */
export const CHANNEL_WRITE_ACTION_STAGE: Record<ChannelPushAction, number> = {
  CONFIRM_VEHICLE: 1,
  CHANGE_VEHICLE: 1,
  SEND_TO_VEHICLE: 2,
  MARK_DELIVERED: 3,
  CANCEL: 4,
};

/**
 * Açık iş (WAITING/READY/AWAITING_APPROVAL) bu kadar günden eskiyse EXPIRED olur. Tasarımda yoktu;
 * KVKK/veri minimizasyonu için sabitlendi (ADR 2026-10-30 notu): bayat niyet yazılmaz.
 */
export const CHANNEL_WRITE_STALE_DAYS = 7;

/** FAILED sonrası tekrar bekleme dakikaları; `CHANNEL_WRITE_RETRY_LIMIT`te iş durur. */
export const CHANNEL_WRITE_BACKOFF_MINUTES = [1, 5, 15, 60] as const;
export const CHANNEL_WRITE_RETRY_LIMIT = 5;

/**
 * Bekleme kodları: HATA değil SIRA/önkoşul eksikliğidir. İş BEKLER, `failedStreak` tavanına
 * sayılmaz (aksi hâlde iş "deneme sınırı" ile ölürdü). Yazıcı sonucu ya da claim öncesi kapı
 * bu kodlardan birini verirse servis `waitJob` uygular.
 */
export const CHANNEL_WRITE_WAIT_CODES: readonly string[] = [
  'courier-unmapped',
  'send-not-done',
  'employee-mapping-missing',
  'writer-not-configured',
];
